<template>
  <div>
    <Header title="Gestion des grades" />
    <div class="grades-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Gestion des grades</h1>
          <div class="header-actions">
            <button class="btn btn-primary btn-with-icon" @click="openAddGradeModal">
              <span class="material-icons">add_circle</span>
              <span>Nouveau grade</span>
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher un grade..." 
              @input="filterGrades"
            />
          </div>
        </div>

        <!-- Liste des grades -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des grades...</p>
          </div>
          
          <div v-else-if="filteredGrades.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucun grade trouvé pour la recherche "{{ searchQuery }}"</p>
            <p v-else>Aucun grade disponible. Veuillez en ajouter un.</p>
          </div>

          <div v-else class="grades-list">
            <div
              v-for="grade in filteredGrades"
              :key="grade.id"
              class="grade-item"
              :class="{ 'selected': selectedGrade && selectedGrade.id === grade.id }"
              @click="selectGrade(grade)"
            >
              <div class="grade-info">
                <span class="grade-icon material-icons">military_tech</span>
                <div class="grade-details">
                  <h3>{{ grade.libelle }}</h3>
                  <p><strong>Code:</strong> {{ grade.code }}</p>
                  <p>{{ grade.description || 'Aucune description' }}</p>
                </div>
              </div>
              <div class="grade-actions">
                <button class="btn-icon btn-edit" @click.stop="editGrade(grade)" title="Modifier ce grade">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon btn-delete" @click.stop="confirmDeleteGrade(grade)" title="Supprimer ce grade">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails du grade sélectionné -->
        <div v-if="selectedGrade" class="grade-details-panel">
          <!-- En-tête du panneau de détails -->
          <div class="panel-header">
            <h3>Détails du grade</h3>
            <button class="btn-close" @click="selectedGrade = null">
              <span class="material-icons">close</span>
            </button>
          </div>

          <!-- Informations principales du grade -->
          <div class="panel-content">
            <div class="detail-field">
              <span class="field-label">Code:</span>
              <span class="field-value">{{ selectedGrade.code }}</span>
            </div>
            
            <div class="detail-field">
              <span class="field-label">Libellé:</span>
              <span class="field-value">{{ selectedGrade.libelle }}</span>
            </div>
            
            <div class="detail-field">
              <span class="field-label">Description:</span>
              <span class="field-value">{{ selectedGrade.description || 'Aucune description' }}</span>
            </div>
            
            <div class="detail-field">
              <span class="field-label">Créé le:</span>
              <span class="field-value">{{ formatDate(selectedGrade.createdAt) }}</span>
            </div>
            
            <div class="detail-field">
              <span class="field-label">Modifié le:</span>
              <span class="field-value">{{ formatDate(selectedGrade.updatedAt) }}</span>
            </div>
          </div>
          
          <!-- Section des agents associés -->
          <div class="panel-section">
            <!-- En-tête de la section agents -->
            <div class="section-header">
              <h4>
                <span class="material-icons">group</span>
                Agents associés
              </h4>
              <span class="agent-count">{{ associatedAgents.length }} agent(s)</span>
            </div>
            
            <!-- Indicateur de chargement -->
            <div v-if="loadingAgents" class="loading-indicator">
              <div class="spinner"></div>
              <p>Chargement des agents...</p>
            </div>
            
            <!-- Message si aucun agent associé -->
            <div v-else-if="!associatedAgents.length" class="no-results">
              <div class="empty-state">
                <span class="material-icons empty-icon">person_off</span>
                <p>Aucun agent n'est associé à ce grade</p>
              </div>
            </div>
            
            <!-- Liste des agents associés -->
            <div v-else class="agents-list">
              <div 
                v-for="agent in associatedAgents" 
                :key="agent.id" 
                class="agent-item" 
                @click="goToAgentDetail(agent.num_personnel)"
              >
                <!-- Informations principales de l'agent -->
                <div class="agent-info">
                  <!-- Avatar de l'agent (photo ou icône) -->
                  <div class="agent-avatar">
                    <img 
                      :src="agent.photo || require('@/assets/images/user-default.png')" 
                      :alt="agent.num_personnel || 'Agent'" 
                      class="agent-photo"
                      @error="handleImageError"
                    >
                  </div>
                  
                  <!-- Détails de l'agent (nom et informations secondaires) -->
                  <div class="agent-details">
                    <div class="agent-name">
                      {{ agent.nom || 'Agent' }}
                    </div>
                    <div class="agent-secondary-info">
                      <span class="agent-matricule">{{ agent.matricule }}</span>
                      <span class="agent-fonction">{{ agent.fonction }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Informations de contact de l'agent -->
                <div class="agent-contact">
                  <div class="contact-item" v-if="agent.email">
                    <span class="material-icons contact-icon">email</span>
                    <span class="contact-value">{{ agent.email }}</span>
                  </div>
                  
                  <div class="contact-item" v-if="agent.telephone">
                    <span class="material-icons contact-icon">phone</span>
                    <span class="contact-value">{{ agent.telephone }}</span>
                  </div>
                  
                  <div class="contact-item" v-if="agent.sexe">
                    <span class="material-icons contact-icon">
                      {{ agent.sexe === 'M' ? 'male' : 'female' }}
                    </span>
                    <span class="contact-value">
                      {{ agent.sexe === 'M' ? 'Homme' : 'Femme' }}
                    </span>
                  </div>
                </div>
                
                <!-- Actions pour l'agent -->
                <div class="agent-actions">
                  <span class="material-icons">chevron_right</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour l'ajout/modification de grade -->
    <dialog ref="gradeModal" class="grade-modal">
      <div class="modal-header">
        <h3>{{ currentAction === 'create' ? 'Ajouter un grade' : 'Modifier le grade' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-form-container">
          <form @submit.prevent="saveGrade" id="gradeForm">
            <div class="form-group">
              <label for="code">Code*</label>
              <input 
                type="text" 
                id="code" 
                v-model="formData.code" 
                required 
                maxlength="20"
                :class="{ 'error': formErrors.code }"
                placeholder="Ex: GRD001"
              />
              <span class="error-message" v-if="formErrors.code">{{ formErrors.code }}</span>
            </div>
            <div class="form-group">
              <label for="libelle">Libellé*</label>
              <input 
                type="text" 
                id="libelle" 
                v-model="formData.libelle" 
                required 
                placeholder="Ex: Directeur, Chef de service, Technicien..."
                :class="{ 'error': formErrors.libelle }"
              />
              <span class="error-message" v-if="formErrors.libelle">{{ formErrors.libelle }}</span>
            </div>
            <div class="form-group">
              <label for="description">Description</label>
              <textarea 
                id="description" 
                v-model="formData.description" 
                rows="3"
                placeholder="Décrivez les responsabilités et attributions de ce grade..."
              ></textarea>
            </div>
          </form>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">Annuler</button>
        <button class="btn btn-primary" form="gradeForm">
          {{ currentAction === 'create' ? 'Ajouter' : 'Enregistrer' }}
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer le grade "{{ gradeToDelete?.libelle }}" ?</p>
        <p class="warning">Cette action est irréversible.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Annuler</button>
        <button class="btn btn-danger" @click="deleteGrade">Supprimer</button>
      </div>
    </dialog>

    <!-- Notification toast -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-icon">
        <span class="material-icons">
          {{ notification.type === 'success' ? 'check_circle' : 
             notification.type === 'error' ? 'error' : 
             notification.type === 'info' ? 'info' : 'warning' }}
        </span>
      </div>
      <div class="notification-content">
        <h4>{{ notification.title }}</h4>
        <p>{{ notification.message }}</p>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import Header from '../components/Header.vue';

// Router pour la navigation
const router = useRouter();

// États réactifs
const grades = ref([]);
const filteredGrades = ref([]);
const selectedGrade = ref(null);
const associatedAgents = ref([]);
const loading = ref(false);
const loadingAgents = ref(false);
const searchQuery = ref('');

// Modals
const gradeModal = ref(null);
const confirmDeleteModal = ref(null);

// État du formulaire
const formData = reactive({
  code: '',
  libelle: '',
  description: ''
});
const formErrors = reactive({
  code: '',
  libelle: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');
const gradeToDelete = ref(null);

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

// Charger les grades au montage du composant
onMounted(async () => {
  await loadGrades();
});

// Méthodes
async function loadGrades() {
  loading.value = true;
  try {
    const response = await api.getGrades();
    if (response.data && response.data.success) {
      grades.value = response.data.data || [];
      filteredGrades.value = [...grades.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des grades:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les grades.');
  } finally {
    loading.value = false;
  }
}

function filterGrades() {
  if (!searchQuery.value.trim()) {
    filteredGrades.value = [...grades.value];
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredGrades.value = grades.value.filter(grade => 
    grade.code.toLowerCase().includes(query) ||
    grade.libelle.toLowerCase().includes(query) || 
    (grade.description && grade.description.toLowerCase().includes(query))
  );
}

async function selectGrade(grade) {
  selectedGrade.value = grade;
  await loadAssociatedAgents(grade.id);
}

async function loadAssociatedAgents(gradeId) {
  loadingAgents.value = true;
  associatedAgents.value = [];
  
  try {
    // Utilisation de l'endpoint /api/parametres/grades/:id/agents pour les agents par grade
    const response = await api.getAgentsByGrade(gradeId);
    console.log('Réponse API agents par grade:', response);
    
    if (response.data && response.data.success) {
      // On récupère les agents depuis la propriété data de la réponse
      associatedAgents.value = response.data.data || [];
      
      // Vérifier si on a des informations de pagination
      if (response.data.pagination) {
        // Ici on pourrait implémenter un système de pagination si nécessaire
        console.log('Pagination:', response.data.pagination);
        // Ex: totalAgents.value = response.data.pagination.total;
      }
      
      // Afficher une notification de succès si des agents sont trouvés
      if (associatedAgents.value.length > 0) {
        showNotification('success', 'Agents chargés', `${associatedAgents.value.length} agent(s) associé(s) à ce grade.`);
      }
    } else {
      // Si l'API a répondu mais avec success = false
      showNotification('warning', 'Attention', response.data.message || 'Aucun agent trouvé pour ce grade.');
    }
  } catch (error) {
    console.error('Erreur lors du chargement des agents:', error);
    let errorMessage = 'Impossible de charger les agents associés à ce grade.';
    
    // Récupérer le message d'erreur spécifique si disponible
    if (error.response && error.response.data) {
      errorMessage = error.response.data.message || errorMessage;
    }
    
    showNotification('error', 'Erreur', errorMessage);
  } finally {
    loadingAgents.value = false;
  }
}

// Navigation vers la fiche détaillée d'un agent
function goToAgentDetail(agentId) {
  if (!agentId) return;
  router.push(`/agent/${agentId}`);
}

function openAddGradeModal() {
  resetForm();
  currentAction.value = 'create';
  gradeModal.value.showModal();
}

function editGrade(grade) {
  resetForm();
  currentAction.value = 'update';
  formData.code = grade.code || '';
  formData.libelle = grade.libelle;
  formData.description = grade.description || '';
  formData.id = grade.id;
  gradeModal.value.showModal();
}

function confirmDeleteGrade(grade) {
  gradeToDelete.value = grade;
  confirmDeleteModal.value.showModal();
}

function closeModal() {
  gradeModal.value.close();
}

function closeConfirmModal() {
  confirmDeleteModal.value.close();
}

function resetForm() {
  formData.id = null;
  formData.code = '';
  formData.libelle = '';
  formData.description = '';
  Object.keys(formErrors).forEach(key => formErrors[key] = '');
}

async function saveGrade() {
  // Valider le formulaire
  if (!validateForm()) return;
  
  try {
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createGrade(formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Grade créé avec succès');
      }
    } else {
      response = await api.updateGrade(formData.id, formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Grade modifié avec succès');
      }
    }
    
    await loadGrades();
    closeModal();
    
    // Si un grade est sélectionné et qu'il vient d'être modifié, mettre à jour sa sélection
    if (selectedGrade.value && selectedGrade.value.id === formData.id) {
      const updatedGrade = grades.value.find(g => g.id === formData.id);
      if (updatedGrade) {
        selectedGrade.value = updatedGrade;
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du grade:', error);
    showNotification('error', 'Erreur', `Impossible de sauvegarder le grade: ${error.message}`);
  }
}

async function deleteGrade() {
  if (!gradeToDelete.value) return;
  
  try {
    const response = await api.deleteGrade(gradeToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Grade supprimé avec succès');
      
      // Si le grade supprimé était sélectionné, désélectionner
      if (selectedGrade.value && selectedGrade.value.id === gradeToDelete.value.id) {
        selectedGrade.value = null;
      }
      
      await loadGrades();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression du grade:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer le grade: ${error.message}`);
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  // Validation du code
  if (!formData.code.trim()) {
    formErrors.code = 'Le code est requis';
    isValid = false;
  } else if (formData.code.length > 20) {
    formErrors.code = 'Le code ne peut pas dépasser 20 caractères';
    isValid = false;
  } else {
    formErrors.code = '';
  }
  
  // Validation du libellé
  if (!formData.libelle.trim()) {
    formErrors.libelle = 'Le libellé est requis';
    isValid = false;
  } else {
    formErrors.libelle = '';
  }
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  // Masquer automatiquement après la durée spécifiée
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

// Gestion des erreurs d'image
function handleImageError(event) {
  event.target.src = require('@/assets/images/user-default.png');
}
</script>

<style scoped>
.grades-view {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

/* Header et navigation */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e6ed;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button:hover {
  color: var(--turquoise-deep);
  transform: translateX(-3px);
}

.back-button .material-icons {
  margin-right: 6px;
  font-size: 18px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Bouton avec icône */
.btn-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 5px 12px rgba(21, 87, 81, 0.3);
  transform: translateY(-2px);
}

.container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 992px) {
  .container {
    grid-template-columns: 2fr 1fr;
  }
  
  .view-header, .search-section, .loading-indicator, .no-results {
    grid-column: span 2;
  }
}

/* En-tête */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.view-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px 15px;
}

.search-input .material-icons {
  color: #777;
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 16px;
  outline: none;
}

/* Liste des grades */
.grades-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.grade-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  background-color: white;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 10px;
}

.grade-item:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.grade-item.selected {
  border-left-color: var(--turquoise-primary);
  background-color: var(--turquoise-pale);
}

.grade-info {
  display: flex;
  align-items: center;
}

.grade-icon {
  color: var(--turquoise-primary);
  margin-right: 15px;
  font-size: 24px;
}

.grade-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.grade-details p {
  margin: 0;
  color: #777;
  font-size: 14px;
}

.grade-actions {
  display: flex;
  gap: 10px;
}

.btn-icon {
  border: none;
  background: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 5px;
}

.btn-edit {
  color: var(--turquoise-primary);
}

.btn-delete {
  color: var(--coral-soft);
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

.btn-edit:hover {
  background-color: rgba(48, 199, 181, 0.1);
}

.btn-delete:hover {
  background-color: rgba(255, 122, 104, 0.1);
}

/* Panneau de détails */
.grade-details-panel {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  margin-top: 0;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h3 {
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #3c4043;
  font-size: 1.1rem;
}

.panel-header .material-icons {
  margin-right: 10px;
  color: var(--turquoise-primary);
}

.panel-actions {
  display: flex;
  gap: 10px;
}

.panel-content {
  padding: 20px 25px;
}

.detail-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 14px;
  color: #777;
  margin-bottom: 3px;
}

.field-value {
  font-size: 16px;
  color: #333;
}

.panel-section {
  padding: 20px 25px;
  border-top: 1px solid #ddd;
}

.panel-section h4 {
  margin: 0 0 15px;
  font-size: 16px;
  font-weight: 500;
}

.agents-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.agent-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e6ed;
  transition: all 0.2s ease;
  cursor: pointer;
  gap: 10px;
}

.agent-item:hover {
  background-color: #f5f7fa;
}

.agent-item:last-child {
  border-bottom: none;
}

.agent-info {
  display: flex;
  align-items: center;
  min-width: 200px;
  max-width: 30%;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--turquoise-pale);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  overflow: hidden;
  flex-shrink: 0;
}

.agent-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-avatar .material-icons {
  color: var(--turquoise-primary);
  font-size: 20px;
}

.agent-details {
  flex: 1;
}

.agent-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.agent-secondary-info {
  display: flex;
  gap: 10px;
  font-size: 0.8rem;
}

.agent-matricule {
  color: var(--turquoise-primary);
  font-weight: 500;
}

.agent-fonction {
  color: #666;
  font-style: italic;
}

.agent-contact {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 10px;
  font-size: 0.8rem;
  color: #666;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.contact-icon {
  font-size: 16px;
  color: var(--turquoise-primary);
}

.contact-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(48, 199, 181, 0.2);
  border-top: 3px solid var(--turquoise-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #777;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: #aaa;
}

/* Modal */
.grade-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

.grade-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

.confirm-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.confirm-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-body {
  padding: 0;
  max-height: 70vh;
  overflow: hidden;
}

.modal-form-container {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.modal-footer {
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Formulaire */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #3c4043;
  font-size: 0.95rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dde2e6;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-group input:focus, .form-group textarea:focus {
  border-color: var(--turquoise-primary);
  outline: none;
}

.form-group input.error {
  border-color: var(--coral-soft);
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
}

/* Boutons */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  transition: background-color 0.2s;
}

.btn .material-icons {
  font-size: 20px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--turquoise-dark);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn-danger:hover {
  background-color: #FF6A58;
}

/* Notification */
.notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  z-index: 1000;
}

.notification.success {
  border-left: 4px solid var(--green-emerald);
}

.notification.error {
  border-left: 4px solid var(--coral-soft);
}

.notification.warning {
  border-left: 4px solid #FF9800;
}

.notification.info {
  border-left: 4px solid var(--turquoise-primary);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: var(--green-emerald);
}

.notification.error .material-icons {
  color: var(--coral-soft);
}

.notification.warning .material-icons {
  color: #FF9800;
}

.notification.info .material-icons {
  color: var(--turquoise-primary);
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

.warning {
  color: var(--coral-soft);
  font-weight: 500;
}

/* Styles responsifs pour les modales */
@media (max-width: 768px) {
  .grade-modal,
  .confirm-modal {
    width: 95vw;
    max-width: none;
    margin: 10px;
    max-height: 90vh;
  }
  
  .modal-body {
    max-height: 60vh;
  }
  
  .modal-form-container {
    padding: 15px;
    max-height: 60vh;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .form-group input,
  .form-group textarea {
    font-size: 16px; /* Évite le zoom sur iOS */
    padding: 12px;
  }
  
  .modal-footer {
    padding: 12px 15px;
    flex-direction: column;
    gap: 8px;
  }
  
  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .grade-modal,
  .confirm-modal {
    width: 98vw;
    margin: 5px;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 15px;
  }
  
  .modal-header h3 {
    font-size: 16px;
  }
  
  .modal-body {
    max-height: 50vh;
  }
  
  .modal-form-container {
    padding: 10px;
    max-height: 50vh;
  }
  
  .form-group {
    margin-bottom: 12px;
  }
  
  .form-group label {
    font-size: 14px;
  }
  
  .form-group input,
  .form-group textarea {
    font-size: 14px;
    padding: 10px;
  }
  
  .modal-footer {
    padding: 10px;
  }
  
  .btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}

/* Amélioration de l'affichage des agents */
.agent-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.agent-avatar {
  position: relative;
}

.agent-avatar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  pointer-events: none;
}

/* Styles personnalisés pour les barres de défilement avec les couleurs turquoise */
/* Pour WebKit (Chrome, Safari, Edge) */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--turquoise-primary);
  border-radius: 4px;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--turquoise-deep);
}

::-webkit-scrollbar-corner {
  background: #f1f5f9;
}

/* Styles spécifiques pour les conteneurs avec défilement */
.modal-form-container::-webkit-scrollbar,
.agents-list::-webkit-scrollbar {
  width: 6px;
}

.modal-form-container::-webkit-scrollbar-track,
.agents-list::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.5);
  border-radius: 3px;
}

.modal-form-container::-webkit-scrollbar-thumb,
.agents-list::-webkit-scrollbar-thumb {
  background: rgba(48, 199, 181, 0.7);
  border-radius: 3px;
}

.modal-form-container::-webkit-scrollbar-thumb:hover,
.agents-list::-webkit-scrollbar-thumb:hover {
  background: var(--turquoise-primary);
}

/* Pour Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-primary) #f1f5f9;
}

.modal-form-container,
.agents-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(48, 199, 181, 0.7) rgba(241, 245, 249, 0.5);
}
</style>
